/**
 * PURPOSE: La ÚNICA fuente de estado operacional del Centro de Programación (FTR-0028 WG3, D-23).
 * RESPONSIBILITIES: Cargar contexto, borrador y propiedad en la caché compartida de React Query,
 *   exponer la versión vigente del borrador y concentrar las escrituras con su invalidación.
 *
 *   CUATRO VISTAS, UNA CONSULTA. `Por Grupo`, `Por Fecha`, `Calendario` y `Por Sede` no son cuatro
 *   pantallas con cuatro cargas: son cuatro proyecciones de este estado. Cambiar de vista no dispara
 *   red, y un cambio hecho en una vista se ve en las otras tres porque hay una sola caché.
 *
 *   NADA DE OPTIMISTIC UPDATE. Una programación, una materialización o una reorganización pueden
 *   quedar parcialmente bloqueadas por reglas que solo el servidor conoce (actividad competitiva,
 *   propiedad, sede inválida). Pintar el resultado antes de saberlo mostraría un estado que no
 *   existe, así que se escribe, se refetchea y se muestra lo que el backend confirmó.
 *
 *   LA VERSIÓN VIAJA SIEMPRE. `draft.version` sube con CADA escritura del borrador, incluida una de
 *   programación. Se lee del último `GET /draft` y se manda en `If-Match`; si otra persona escribió
 *   entre medias el backend responde 409 y aquí no se reintenta: se recarga y se vuelve a revisar.
 * COLLABORATORS: services/programmingCenterService.js, ProgrammingCenterProvider.jsx,
 *   utils/programmingCenterErrors.js.
 * ROUTE: winscore/src/hooks/useProgrammingCenterData.js
 */
import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";

import {
  getPhaseGraph,
  getProgrammingCenterContext,
  getProgrammingCenterDraft,
  getProgrammingCenterOwnership,
} from "../services/programmingCenterService.js";
import { parseProgrammingCenterError } from "../utils/programmingCenterErrors.js";

export const PROGRAMMING_CENTER_QUERY_ROOT = "programming-center";

export const programmingCenterKeys = {
  root: [PROGRAMMING_CENTER_QUERY_ROOT],
  context: (idevent, idsport) => [PROGRAMMING_CENTER_QUERY_ROOT, "context", Number(idevent), Number(idsport)],
  draft: (idevent, idsport) => [PROGRAMMING_CENTER_QUERY_ROOT, "draft", Number(idevent), Number(idsport)],
  ownership: (idevent, idsport) => [PROGRAMMING_CENTER_QUERY_ROOT, "ownership", Number(idevent), Number(idsport)],
  graph: (idevent, idsport) => [PROGRAMMING_CENTER_QUERY_ROOT, "graph", Number(idevent), Number(idsport)],
};

/**
 * "Todavía no hay fixture" NO es un error: es el estado inicial de todo deporte. Se traduce a `null`
 * para que la pantalla ofrezca "Armar fixture" en vez de una alerta roja.
 */
const loadDraft = async ({ idevent, idsport }) => {
  try {
    return await getProgrammingCenterDraft({ idevent, idsport });
  } catch (error) {
    if (parseProgrammingCenterError(error).code === "DRAFT_NOT_FOUND") return null;
    throw error;
  }
};

const QUERY_OPTIONS = {
  // Los errores del Centro son decisiones (403, 404, 409), no fallos de red: reintentarlos solo
  // retrasa el mensaje que el administrador necesita leer.
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 15 * 1000,
};

export const useProgrammingCenterData = ({ idevent, idsport, enabled = true }) => {
  const queryClient = useQueryClient();
  const scope = useMemo(
    () => ({ idevent: Number(idevent), idsport: Number(idsport) }),
    [idevent, idsport]
  );
  const ready = enabled && Number.isInteger(scope.idevent) && Number.isInteger(scope.idsport);

  const contextQuery = useQuery(
    programmingCenterKeys.context(scope.idevent, scope.idsport),
    () => getProgrammingCenterContext(scope),
    { ...QUERY_OPTIONS, enabled: ready }
  );

  const draftQuery = useQuery(
    programmingCenterKeys.draft(scope.idevent, scope.idsport),
    () => loadDraft(scope),
    { ...QUERY_OPTIONS, enabled: ready }
  );

  // La propiedad se consulta solo cuando el contexto ya dijo que existen partidos externos: el banner
  // no puede aparecer antes, y pedirla siempre sería una consulta inútil en la mayoría de eventos.
  const hasExternal = Number(contextQuery.data?.matches?.external) > 0;
  const ownershipQuery = useQuery(
    programmingCenterKeys.ownership(scope.idevent, scope.idsport),
    () => getProgrammingCenterOwnership(scope),
    { ...QUERY_OPTIONS, enabled: ready && hasExternal }
  );

  // El grafo de fases solo existe cuando hay partidos MATERIALIZADOS: antes no hay aristas que
  // recorrer y pedirlo sería una consulta vacía en todo evento que aún está en diseño.
  const hasMaterialized = Number(draftQuery.data?.counts?.materialized) > 0;
  const graphQuery = useQuery(
    programmingCenterKeys.graph(scope.idevent, scope.idsport),
    () => getPhaseGraph(scope),
    { ...QUERY_OPTIONS, enabled: ready && hasMaterialized }
  );

  const invalidateDraft = useCallback(
    () => queryClient.invalidateQueries(programmingCenterKeys.draft(scope.idevent, scope.idsport)),
    [queryClient, scope.idevent, scope.idsport]
  );

  const invalidateContext = useCallback(
    () => queryClient.invalidateQueries(programmingCenterKeys.context(scope.idevent, scope.idsport)),
    [queryClient, scope.idevent, scope.idsport]
  );

  const invalidateOwnership = useCallback(
    () => queryClient.invalidateQueries(programmingCenterKeys.ownership(scope.idevent, scope.idsport)),
    [queryClient, scope.idevent, scope.idsport]
  );

  const invalidateGraph = useCallback(
    () => queryClient.invalidateQueries(programmingCenterKeys.graph(scope.idevent, scope.idsport)),
    [queryClient, scope.idevent, scope.idsport]
  );

  /**
   * Refresco tras una escritura. `scope: "structure"` recarga también el contexto y la propiedad:
   * materializar y reorganizar cambian `config_category`, los recuentos de partidos y los externos
   * detectados como duplicados, no solo las filas.
   */
  const refresh = useCallback(
    async ({ scope: refreshScope = "draft" } = {}) => {
      const jobs = [invalidateDraft()];
      if (refreshScope === "structure") {
        jobs.push(invalidateContext(), invalidateOwnership(), invalidateGraph());
      }
      // Una recomputación cambia participantes, así que el borrador hidratado también cambia: el
      // grafo NUNCA se refresca solo, siempre junto a las filas que muestran esos participantes.
      if (refreshScope === "graph") jobs.push(invalidateGraph());
      await Promise.all(jobs);
    },
    [invalidateContext, invalidateDraft, invalidateGraph, invalidateOwnership]
  );

  const version = Number(draftQuery.data?.version) || null;

  return {
    scope,
    ready,
    context: contextQuery.data || null,
    contextQuery,
    draft: draftQuery.data || null,
    draftQuery,
    ownership: ownershipQuery.data || null,
    ownershipQuery,
    graph: graphQuery.data || null,
    graphQuery,
    version,
    hasDraft: Boolean(draftQuery.data?.draft?.exists ?? draftQuery.data),
    loading: contextQuery.isLoading || draftQuery.isLoading,
    fetching: contextQuery.isFetching || draftQuery.isFetching || ownershipQuery.isFetching,
    error: contextQuery.error || draftQuery.error || null,
    refresh,
    invalidateDraft,
    invalidateContext,
    invalidateOwnership,
    invalidateGraph,
  };
};
